import { useEffect, useMemo, useCallback } from 'react'
import { FaSync, FaUser, FaMapMarkerAlt, FaCalendar, FaClock, FaDollarSign, FaClipboardList, FaExclamationTriangle, FaCog } from 'react-icons/fa'
import { useServiceRequests, useAcceptServiceRequest, useDenyServiceRequest, useStartService, useCompleteService } from '@/hooks/useRequests'
import { useQuoteRequests } from '@/hooks/useQuoteRequests'
import { useNavigate } from 'react-router-dom'
import Button from '@/components/common/Button/Button'
import { useProfileStatus } from '@/hooks/useProfile'
import routes from '@/routes/routes'
import ProviderOnboarding from '@/components/onboarding/ProviderOnboarding'
import Spinner from '@/components/common/Spinner/Spinner'
import './ProviderRequests.css'

const getStatusColor = (status: string, type?: 'service_request' | 'quote_request') => {
    if (type === 'quote_request') {
        const quoteMap: Record<string, string> = {
            pending: '#F59E0B',
            sent: '#3B82F6',
            accepted: '#10B981',
            declined: '#6B7280',
            expired: '#6B7280',
            cancelled: '#EF4444',
        }
        return quoteMap[status] || '#6B7280'
    }
    
    const map: Record<string, string> = {
        slot_blocked: '#F59E0B',
        pending: '#F59E0B',
        approved: '#3B82F6',
        accepted: '#10B981',
        payment_submitted: '#8B5CF6',
        confirmed: '#10B981',
        in_progress: '#8B5CF6',
        completed: '#10B981',
        finished: '#10B981',
        cancelled_by_provider: '#EF4444',
        cancelled_by_client: '#EF4444',
        denied_by_provider: '#6B7280',
    }
    return map[status] || '#6B7280'
}

const getStatusText = (status: string, type?: 'service_request' | 'quote_request') => {
    if (type === 'quote_request') {
        const quoteMap: Record<string, string> = {
            pending: 'Cotización Pendiente',
            sent: 'Cotización Enviada',
            accepted: 'Cotización Aceptada',
            declined: 'Cotización Rechazada',
            expired: 'Cotización Expirada',
            cancelled: 'Cotización Cancelada',
        }
        return quoteMap[status] || status
    }
    
    const map: Record<string, string> = {
        slot_blocked: 'Nueva Solicitud',
        pending: 'Pendiente',
        approved: 'Aprobado - Esperando Pago',
        accepted: 'Aceptado',
        payment_submitted: 'Pago Enviado',
        confirmed: 'Confirmado',
        in_progress: 'En Progreso',
        completed: 'Completado',
        finished: 'Finalizado',
        cancelled_by_provider: 'Cancelado por Ti',
        cancelled_by_client: 'Cancelado por Cliente',
        denied_by_provider: 'Rechazado',
    }
    return map[status] || status
}

const ProviderRequests = () => {
    const { data: serviceRequests, isLoading: isLoadingServiceRequests, isError: isErrorServiceRequests, refetch: refetchServiceRequests } = useServiceRequests()
    const { data: quoteRequests, isLoading: isLoadingQuoteRequests, isError: isErrorQuoteRequests, refetch: refetchQuoteRequests } = useQuoteRequests()
    const { data: profileStatus, isLoading: statusLoading, refetch: refetchStatus } = useProfileStatus()
    const accept = useAcceptServiceRequest()
    const deny = useDenyServiceRequest()
    const start = useStartService()
    const complete = useCompleteService()
    const navigate = useNavigate()
    
    const isLoading = isLoadingServiceRequests || isLoadingQuoteRequests
    const isError = isErrorServiceRequests || isErrorQuoteRequests

    // Refetch profile status when component mounts to ensure fresh data
    useEffect(() => {
        refetchStatus()
    }, [refetchStatus])

    // Combine and sort by creation date (newest first) - MUST be called before any conditional returns
    const list = useMemo(() => {
        const serviceList = (serviceRequests || []).map((r: any) => ({
            ...r,
            type: 'service_request' as const,
            displayId: r.id,
            displayDate: r.created,
        }))
        
        const quoteList = (quoteRequests || []).map((q: any) => ({
            ...q,
            type: 'quote_request' as const,
            displayId: q.id,
            displayDate: q.created,
            // Map quote request fields to match display format
            service_name: q.service_name || q.service?.name || 'Servicio',
            receiver: q.requester ? {
                profile: {
                    first_name: q.requester_name?.split(' ')[0] || '',
                    last_name: q.requester_name?.split(' ').slice(1).join(' ') || '',
                }
            } : undefined,
            location_address: q.district_details?.name || 'Distrito no especificado',
        }))
        
        const combined = [...serviceList, ...quoteList]
        return combined.sort((a, b) => {
            const dateA = new Date(a.displayDate).getTime()
            const dateB = new Date(b.displayDate).getTime()
            return dateB - dateA
        })
    }, [serviceRequests, quoteRequests])
    
    const refetch = useCallback(() => {
        refetchServiceRequests()
        refetchQuoteRequests()
    }, [refetchServiceRequests, refetchQuoteRequests])

    // Check if provider needs onboarding
    const needsOnboarding = profileStatus && profileStatus.is_provider && (!profileStatus.has_services || !profileStatus.has_districts)

    // Debug logging
    useEffect(() => {
        if (profileStatus) {
            console.log('Profile Status:', {
                is_profile_configured: profileStatus.is_profile_configured,
                is_verified: profileStatus.is_verified,
                has_personal_info: profileStatus.has_personal_info,
                has_districts: profileStatus.has_districts,
                has_services: profileStatus.has_services,
                has_pricing: profileStatus.has_pricing,
                has_availability: profileStatus.has_availability,
            })
        }
    }, [profileStatus])

    const isProfileComplete = profileStatus?.is_profile_configured && profileStatus?.is_verified

    // Show onboarding if needed (Main component will handle hiding Navbar/Footer)
    if (statusLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <Spinner />
            </div>
        )
    }

    if (needsOnboarding) {
        return <ProviderOnboarding />
    }

    return (
        <div className="provider-requests-page">
            <div className="provider-requests-header">
                <div className="provider-requests-header-content">
                    <h1 className="provider-requests-title">Mis Solicitudes</h1>
                    <p className="provider-requests-subtitle">{list.length} solicitud{list.length !== 1 ? 'es' : ''}</p>
                </div>
                <Button variant="secondary" size="md" onClick={() => refetch()}>
                    <FaSync /> Actualizar
                </Button>
            </div>

            {/* Configuration incomplete notification */}
            {!isProfileComplete && !statusLoading && (
                <div className="provider-requests-notification">
                    <div className="provider-requests-notification-content">
                        <div className="provider-requests-notification-icon">
                            <FaExclamationTriangle />
                        </div>
                        <div className="provider-requests-notification-text">
                            <h3 className="provider-requests-notification-title">Completa tu configuración</h3>
                            <p className="provider-requests-notification-desc">
                                {!profileStatus?.is_profile_configured && !profileStatus?.is_verified
                                    ? 'Necesitas completar tu perfil y verificarte para recibir solicitudes de servicio.'
                                    : !profileStatus?.is_profile_configured
                                    ? 'Necesitas completar tu perfil para recibir solicitudes de servicio.'
                                    : 'Necesitas verificar tu identidad para recibir solicitudes de servicio.'}
                            </p>
                        </div>
                        <Button 
                            variant="primary" 
                            size="md"
                            onClick={() => navigate(routes.providerSetup)}
                        >
                            <FaCog /> Ir a Configuración
                        </Button>
                    </div>
                </div>
            )}

            <div className="provider-requests-container">
                {isLoading ? (
                    <div className="provider-requests-message">Cargando...</div>
                ) : isError ? (
                    <div className="provider-requests-message">Error al cargar solicitudes</div>
                ) : (
                <div className="provider-requests-list">
                    {list.map((r: any) => (
                        <div key={`${r.type}-${r.id}`} className="provider-requests-card">
                            <div className="provider-requests-card-header">
                                <h3 className="provider-requests-service-name">
                                    {r.service_name || r.service?.name}
                                    {r.type === 'quote_request' && <span style={{ fontSize: '0.85em', color: '#6B7280', marginLeft: '8px' }}>(Cotización)</span>}
                                </h3>
                                <span className="provider-requests-badge" style={{ backgroundColor: getStatusColor(r.status, r.type) }}>
                                    {getStatusText(r.status, r.type)}
                                </span>
                            </div>
                            <div className="provider-requests-details">
                                <div className="provider-requests-detail-item">
                                    <FaUser className="provider-requests-icon" />
                                    <div>
                                        <div className="provider-requests-detail-label">Cliente</div>
                                        <div className="provider-requests-detail-value">{r.receiver?.profile?.first_name || 'Usuario Nuevo'} {r.receiver?.profile?.last_name}</div>
                                    </div>
                                </div>
                                <div className="provider-requests-detail-item">
                                    <FaMapMarkerAlt className="provider-requests-icon" />
                                    <div>
                                        <div className="provider-requests-detail-label">Ubicación</div>
                                        <div className="provider-requests-detail-value">{r.location_address}</div>
                                    </div>
                                </div>
                                <div className="provider-requests-detail-item">
                                    <FaCalendar className="provider-requests-icon" />
                                    <div>
                                        <div className="provider-requests-detail-label">Fecha</div>
                                        <div className="provider-requests-detail-value">
                                            {(() => {
                                                // Parse date string as local date to avoid timezone shift
                                                const dateStr = r.scheduled_date
                                                let date: Date
                                                if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
                                                    // Parse as local date (YYYY-MM-DD)
                                                    const [year, month, day] = dateStr.split('-').map(Number)
                                                    date = new Date(year, month - 1, day)
                                                } else {
                                                    // Fallback to standard parsing
                                                    date = new Date(dateStr)
                                                }
                                                return date.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                                            })()}
                                        </div>
                                    </div>
                                </div>
                                {r.scheduled_hour !== undefined && (
                                    <div className="provider-requests-detail-item">
                                        <FaClock className="provider-requests-icon" />
                                        <div>
                                            <div className="provider-requests-detail-label">Horario</div>
                                            <div className="provider-requests-detail-value">{(() => {
                                                const start = r.scheduled_hour
                                                const duration = r.estimated_duration_hours || r.estimated_duration || 1
                                                const end = start + duration
                                                return `${String(start).padStart(2, '0')}:00 - ${String(end).padStart(2, '0')}:00 (${duration}h)`
                                            })()}</div>
                                        </div>
                                    </div>
                                )}
                                <div className="provider-requests-detail-item">
                                    <FaDollarSign className="provider-requests-icon" />
                                    <div>
                                        <div className="provider-requests-detail-label">Precio Total</div>
                                        <div className="provider-requests-detail-value provider-requests-price">
                                            {r.type === 'quote_request' 
                                                ? (r.status === 'pending' ? 'Cotización pendiente' : `S/ ${r.quoted_total || 0}`)
                                                : `S/ ${r.proposed_price || 0}`}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {r.description && (
                                <div className="provider-requests-description">
                                    <div className="provider-requests-description-label">Descripción del servicio:</div>
                                    <p className="provider-requests-description-text">{r.description}</p>
                                </div>
                            )}
                            {r.type !== 'quote_request' && (
                            <div className="provider-requests-actions">
                                {r.has_conversation && r.conversation_id && (
                                    <Button variant="secondary" size="md" onClick={() => navigate(routes.chat.replace(':id', String(r.conversation_id)))}>
                                        Abrir Chat
                                    </Button>
                                )}
                                {(r.status === 'slot_blocked' || r.status === 'pending') && (
                                    <>
                                        <Button variant="danger" size="md" disabled={deny.isPending} onClick={() => deny.mutate({ requestId: r.id, reason: 'No disponible' })}>
                                            Rechazar
                                        </Button>
                                        <Button variant="primary" size="md" disabled={accept.isPending} onClick={() => accept.mutate(r.id)}>
                                            Aceptar Solicitud
                                        </Button>
                                    </>
                                )}
                                {r.status === 'accepted' && (
                                    <Button variant="primary" size="md" disabled={start.isPending} onClick={() => start.mutate(r.id)}>
                                        Iniciar Servicio
                                    </Button>
                                )}
                                {r.status === 'in_progress' && (
                                    <Button variant="success" size="md" disabled={complete.isPending} onClick={() => complete.mutate(r.id)}>
                                        Completar Servicio
                                    </Button>
                                )}
                            </div>
                            )}
                            {r.type === 'quote_request' && (
                                <div className="provider-requests-actions">
                                    {r.can_provider_send_quote && (
                                        <Button variant="primary" size="md" onClick={() => {
                                            navigate(`/provider/quote/${r.id}`)
                                        }}>
                                            Hacer Cotización
                                        </Button>
                                    )}
                                    <Button variant="secondary" size="md" onClick={() => {
                                        navigate(`/provider/quote/${r.id}`)
                                    }}>
                                        Ver Detalles
                                    </Button>
                                </div>
                            )}
                        </div>
                    ))}
                    {list.length === 0 && (
                        <div className="provider-requests-empty">
                            <FaClipboardList style={{ fontSize: '3rem', color: 'var(--text-tertiary)' }} />
                            <div style={{ marginTop: '1rem', fontWeight: 600 }}>No hay solicitudes</div>
                            <div>Las solicitudes de servicios aparecerán aquí</div>
                        </div>
                    )}
                </div>
                )}
            </div>
        </div>
    )
}

export default ProviderRequests


